import { Flex, SidePanel, Text, UploadArea, FilesList } from "@wayflyer/flyui";
import { IconCheckCircle16Line } from "@wayflyer/flyui-icons/16/line";
import dayjs from "dayjs";
import { useMemo } from "react";
import { useIntl } from "react-intl";
import { usePostHog } from "posthog-js/react";
import { formatDate } from "shared-ui";
import type { UploadsState } from "shared-utils";

import { useBankControllerContext } from "../../pages/banking/hooks/bank-controller-context";

import { MESSAGES } from "./message-definitions";

const DEFAULT_GROUP_NAME = "Funding Application Statements";

interface BankStatementsSlideoutProps {
  isOpen: boolean;
}

export const BankStatementsSlideout = ({ isOpen }: BankStatementsSlideoutProps) => {
  const intl = useIntl();
  const posthog = usePostHog();
  const {
    fileUploadProps,
    requiredStatementMonths,
    bankingStatementsGroups,
    handleSubmitStatements,
    closeStatementsSlideout,
  } = useBankControllerContext();

  const previouslyUploadedUploadsState = useMemo<UploadsState<string> | null>(() => {
    const statements = bankingStatementsGroups?.flatMap((group) => group.previouslyUploadedStatements ?? []) ?? [];
    if (!statements.length) {
      return null;
    }
    return {
      uploadsStarted: true,
      uploadsComplete: true,
      uploads: statements.map((s) => ({
        status: "complete" as const,
        file: new File([], s.fileName),
        uploadedFile: s.fileType,
      })),
    };
  }, [bankingStatementsGroups]);

  const statementPeriodStart = useMemo(
    () => formatDate(dayjs().subtract(requiredStatementMonths, "month").toISOString()),
    [requiredStatementMonths]
  );

  const handleDone = async () => {
    const completedUploads = fileUploadProps.uploadsState?.uploads.filter((u) => u.status === "complete") ?? [];
    if (completedUploads.length > 0) {
      await handleSubmitStatements(DEFAULT_GROUP_NAME);
      posthog?.capture("onboarding_bank_file_uploaded", {
        upload_type: "bank_statement",
        file_count: completedUploads.length,
      });
    }
    fileUploadProps.onFilesChanged();
    closeStatementsSlideout();
  };

  const rules = [
    intl.formatMessage(MESSAGES.slideoutRule1),
    intl.formatMessage(MESSAGES.slideoutRule2),
    intl.formatMessage(MESSAGES.slideoutRule3),
  ];

  return (
    <SidePanel
      open={isOpen}
      isModal={false}
      onClose={handleDone}
      closeOnClickElseWhere={false}
      title={intl.formatMessage(MESSAGES.bankStatementsSlideoutTitle)}
      variant="md"
      primaryAction={{
        content: intl.formatMessage(MESSAGES.submitStatementsButton),
        onAction: handleDone,
      }}
    >
      <Flex direction="column" gap="4">
        <Flex direction="column" gap="8">
          <Text color="placeholder">
            {intl.formatMessage(MESSAGES.slideoutRequirementBody, {
              months: requiredStatementMonths,
              date: statementPeriodStart,
            })}
          </Text>

          <Flex direction="column" gap="2">
            <Text fontWeight="semiBold">{intl.formatMessage(MESSAGES.slideoutWhatToIncludeHeading)}</Text>
            {rules.map((rule) => (
              <Flex key={rule} align="flex-start" gap="2">
                <Flex shrink={0} padding={["0.5", "0", "0", "0"]}>
                  <IconCheckCircle16Line color="var(--palette-content-neutral-subtle)" />
                </Flex>
                <Text size="sm">{rule}</Text>
              </Flex>
            ))}
          </Flex>

          <Flex direction="column" gap="2">
            <UploadArea
              testId="bank-statements-slideout-upload-area"
              onFilesAdded={fileUploadProps.onFilesAdded}
              acceptedFileTypes={{ "text/csv": [".csv"], "application/pdf": [".pdf"] }}
              sizeLimitMB={fileUploadProps.sizeLimitMB}
              multiple
            />
            {fileUploadProps.uploadsState && fileUploadProps.uploadsState.uploads.length > 0 && (
              <FilesList uploadsState={fileUploadProps.uploadsState} onFileRemoved={fileUploadProps.onFileRemoved} />
            )}
            {previouslyUploadedUploadsState && <FilesList uploadsState={previouslyUploadedUploadsState} />}
          </Flex>
        </Flex>
      </Flex>
    </SidePanel>
  );
};
